import { computed, runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Drawer } from '@feathr/components';
import { useReactionEffect } from '@feathr/hooks';

import ImportsFilters from './ImportsFilters';
import type { IImportsFilters } from './ImportsFilters/ImportsFilters';

interface IProps {
  close: () => void;
  filters: IImportsFilters;
  isOpen: boolean;
  onApply: (filters: IImportsFilters) => void;
}

function ImportsFiltersDrawer({ close, filters, isOpen, onApply }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const drawerFilters = useLocalObservable<IImportsFilters>(() => ({
    state__in: filters.state__in,
    isOptOut: filters.isOptOut,
    date_operator: filters.date_operator,
    date_created: filters.date_created,
    date_created__gte: filters.date_created__gte,
    date_created__lte: filters.date_created__lte,
  }));

  function setDrawerFilters(newFilters: IImportsFilters): void {
    runInAction(() => {
      Object.assign(drawerFilters, newFilters);
    });
  }

  const filterCount = computed(() => {
    let count = 0;

    // Count state filters
    if (drawerFilters.state__in && drawerFilters.state__in.length > 0) {
      count += drawerFilters.state__in.length;
    }

    // Count type filter (isOptOut)
    if (drawerFilters.isOptOut !== undefined) {
      count += 1;
    }

    // Count date filters - only count if we have actual date values
    if (drawerFilters.date_operator === 'is_between') {
      if (drawerFilters.date_created__gte) {
        count += 1;
      }
      if (drawerFilters.date_created__lte) {
        count += 1;
      }
    } else {
      if (drawerFilters.date_created) {
        count += 1;
      }
    }

    return count;
  });

  useReactionEffect(
    () => filters,
    (filters) => {
      setDrawerFilters(filters);
    },
    {},
    [filters],
  );

  function handleClearFilters(): void {
    const emptyFilters: IImportsFilters = {
      state__in: undefined,
      isOptOut: undefined,
      date_operator: 'is',
      date_created: undefined,
      date_created__gte: undefined,
      date_created__lte: undefined,
    };
    setDrawerFilters(emptyFilters);
    onApply(emptyFilters);
  }

  function handleApplyFilters(): void {
    onApply(drawerFilters);
    close();
  }

  function handleCloseDrawer(): void {
    // When closing without applying, we want to reset the drawer filters to the current filters
    setDrawerFilters(filters);
    close();
  }

  return (
    <Drawer
      isOpen={isOpen}
      name={'imports-filters-drawer'}
      onClose={handleCloseDrawer}
      title={t('Filters')}
    >
      <Drawer.Content addVerticalGap={true}>
        <ImportsFilters filters={drawerFilters} onChange={setDrawerFilters} />
      </Drawer.Content>
      <Drawer.Actions>
        <Button isFullWidth={true} key={'clear'} onClick={handleClearFilters}>
          {t('Clear ({{count}})', { count: filterCount.get() })}
        </Button>
        <Button
          disabled={filterCount.get() === 0}
          isFullWidth={true}
          key={'apply'}
          onClick={handleApplyFilters}
          type={'primary'}
        >
          {t('Apply')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  );
}

export default observer(ImportsFiltersDrawer);
